import { VIETNAM_BOUNDS } from '@/config/map';

function parsePositiveIntEnv(raw: string | undefined): number {
  if (raw == null || raw === '') return 0;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parsePositiveFloatEnv(raw: string | undefined): number {
  if (raw == null || raw === '') return 0;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function parseBoolEnv(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/** Rỗng hoặc không parse được → `null` (dùng mặc định khác). */
function parseOptionalCoord(raw: string | undefined): number | null {
  if (raw == null || raw.trim() === '') return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

function parseOptionalNonEmptyString(raw: string | undefined): string | null {
  if (raw == null || raw.trim() === '') return null;
  return raw.trim();
}

/** Chuỗi phân tách bằng dấu phẩy → mảng; không set hoặc rỗng → `null` (dùng mặc định trong app). */
function parseCommaSeparatedList(raw: string | undefined): string[] | null {
  if (raw == null || raw.trim() === '') return null;
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : null;
}

/** Style vector Vietmap cho bản đồ 2D (`maps/styles/{tm|lm|dm}/style.json`). */
function parseVietmapVectorStyle(raw: string | undefined): 'tm' | 'lm' | 'dm' {
  const v = raw?.trim().toLowerCase();
  if (v === 'lm' || v === 'dm' || v === 'tm') return v;
  return 'tm';
}

export const env = {
  API_BASE_URL: import.meta.env.VITE_API_BASE_URL || '',
  /**
   * Gateway prediction/simulation khi tách khỏi API chính (ví dụ Swagger `:30080` vs dịch vụ `:30070`).
   * Rỗng → FE dùng cùng {@link API_BASE_URL}.
   */
  PREDICTION_SIMULATION_API_BASE_URL:
    (import.meta.env.VITE_PREDICTION_SIMULATION_API_BASE_URL as string | undefined)?.trim() ?? '',
  /**
   * Host netcdf-ingestion (Vite dev: proxy `/api/ingest` → URL này). Rỗng → cùng {@link API_BASE_URL}.
   */
  NETCDF_INGESTION_URL:
    (import.meta.env.VITE_NETCDF_INGESTION_URL as string | undefined)?.trim() ?? '',
  /**
   * Mặc định `true` khi dev build: điền model/hãng/năm thiết bị khi API chưa trả (placeholder `—`),
   * bằng giá trị ổn định theo `station.id`. Production build: `false` trừ khi set `VITE_ENRICH_STATION_SPECS=1`.
   */
  ENRICH_STATION_SPECS: (() => {
    const raw = import.meta.env.VITE_ENRICH_STATION_SPECS as string | undefined;
    if (raw == null || raw.trim() === '') return import.meta.env.DEV;
    return parseBoolEnv(raw);
  })(),
  /**
   * > 0: gọi `POST /api/auth/refresh` định kỳ (ms), dùng khi test cookie/CORS.
   * 0: tắt — dùng lịch theo `exp` JWT (PROACTIVE_REFRESH_BEFORE_EXP_MS).
   */
  AUTH_REFRESH_TEST_INTERVAL_MS: parsePositiveIntEnv(
    import.meta.env.VITE_AUTH_REFRESH_TEST_INTERVAL_MS as string | undefined,
  ),
  GEO_PROCESSING_URL: import.meta.env.VITE_GEO_PROCESSING_URL || '',
  GEE_CLIENT_ID: import.meta.env.VITE_GEE_CLIENT_ID || '',
  WS_URL: import.meta.env.VITE_WS_URL || '',
  NODE_ENV: import.meta.env.MODE,
  IS_DEV: import.meta.env.DEV,
  IS_PROD: import.meta.env.PROD,
  VITE_CESIUM_ION_TOKEN: import.meta.env.VITE_CESIUM_ION_TOKEN,
  /**
   * Khi `true`: ẩn nút "Vận hành" trên header bản đồ (vẫn vào `/ops` trực tiếp nếu cần).
   * Dùng khi demo chỉ muốn giới thiệu dashboard/bản đồ.
   */
  HIDE_OPS_HEADER_LINK: parseBoolEnv(
    import.meta.env.VITE_HIDE_OPS_HEADER_LINK as string | undefined,
  ),
  /**
   * Khi `true`: header user chỉ hiển thị tên hiển thị, không fallback sang email (hữu ích khi quay màn hình).
   * Hữu ích khi không có `VITE_API_BASE_URL` hoặc ẩn PII khi quay phim.
   */
  DEMO_MASK_USER_EMAIL: parseBoolEnv(
    import.meta.env.VITE_DEMO_MASK_USER_EMAIL as string | undefined,
  ),
  /**
   * Khi `true`: ẩn banner giải thích nguồn dữ liệu (Audit, Jobs SAR, Health) — dùng khi trình diễn lãnh đạo.
   */
  UI_SUPPRESS_DATA_SOURCE_HINTS: parseBoolEnv(
    import.meta.env.VITE_UI_SUPPRESS_DATA_SOURCE_HINTS as string | undefined,
  ),
  /**
   * Khi `true`: hiện trên sidebar cả `system-config` và `alert-settings` (hiện vẫn có route placeholder).
   * Mặc định `false` — ẩn hai mục khỏi menu theo khuyến nghị triển khai thực tế.
   */
  OPS_SHOW_PLACEHOLDER_NAV: parseBoolEnv(
    import.meta.env.VITE_OPS_SHOW_PLACEHOLDER_NAV as string | undefined,
  ),
  /**
   * Khi `true`: hiện khối «Hiển thị phân tích» trên sidebar bản đồ (vùng trọng tâm, ngưỡng độ sâu lưới, lớp GEE ngập).
   * Mặc định `false` — ẩn tạm khi chưa chốt nguồn dữ liệu BE / tile GEE.
   */
  SHOW_MAP_ANALYSIS_FILTERS: parseBoolEnv(
    import.meta.env.VITE_SHOW_MAP_ANALYSIS_FILTERS as string | undefined,
  ),
  /**
   * `true`: hiện lớp lưới ô phân tích (5 m) trên bản đồ 2D chồng lên polygon vùng — debug/so sánh.
   * Mặc định `false`: chỉ polygon vùng + viền (mượt, khớp pipeline 3D).
   */
  SHOW_FLOOD_ANALYSIS_GRID: parseBoolEnv(
    import.meta.env.VITE_SHOW_FLOOD_ANALYSIS_GRID as string | undefined,
  ),
  /**
   * `true`: merge map quyền giả lập theo role (seed BE) với GET /api/auth/permissions/me (API ghi đè).
   * Dev/demo khi cần sidebar giống ma trận thật.
   */
  USE_MOCK_PERMISSIONS: parseBoolEnv(
    import.meta.env.VITE_USE_MOCK_PERMISSIONS as string | undefined,
  ),
  /**
   * `true`: khi GET permissions lỗi hoặc map rỗng, dùng {@link getMockMenuPermissionsMap} theo role JWT.
   */
  MOCK_PERMISSIONS_FALLBACK: parseBoolEnv(
    import.meta.env.VITE_MOCK_PERMISSIONS_FALLBACK as string | undefined,
  ),
  /**
   * Trần diện tích AOI phân tích SAR (km²). Không set hoặc ≤ 0 → mặc định 10_000 (spec 6a).
   */
  SAR_AOI_MAX_AREA_KM2: parsePositiveFloatEnv(
    import.meta.env.VITE_SAR_AOI_MAX_AREA_KM2 as string | undefined,
  ),
  /**
   * Ghi đè danh sách URL FeatureCollection vùng ngập (GeoJSON): API, CDN hoặc file tĩnh.
   * Phân tách bằng dấu phẩy. `null` → dùng `DEFAULT_FLOOD_REGION_GEOJSON_URLS` trong `loadMergedFloodRegionGeoJson`.
   */
  FLOOD_REGION_GEOJSON_URLS: parseCommaSeparatedList(
    import.meta.env.VITE_FLOOD_REGION_GEOJSON_URLS as string | undefined,
  ),
  /** Tilemap / style vector Vietmap (`VITE_VIETMAP_TILEMAP_KEY`). */
  VIETMAP_TILEMAP_KEY:
    (import.meta.env.VITE_VIETMAP_TILEMAP_KEY as string | undefined)?.trim() ?? '',
  /**
   * Style nền vector 2D: `tm` (mặc định), `lm` (light), `dm` (dark) — khớp Web SDK v6+ Vietmap.
   * @see https://maps.vietmap.vn/docs/map-api/tilemap/
   */
  VIETMAP_VECTOR_STYLE: parseVietmapVectorStyle(
    import.meta.env.VITE_VIETMAP_VECTOR_STYLE as string | undefined,
  ),
  /**
   * `true` (default): khi job SAR hoàn thành có `aoiPolygon`, gọi API Alpha Earth để điền `imageUrl` cho gallery.
   * `false`: không hydrate Alpha Earth; các slide không có URL ảnh → modal hiển thị trạng thái trống (theo UI).
   */
  SAR_RESULT_ALPHAEARTH_PREVIEW: (() => {
    const raw = import.meta.env.VITE_SAR_RESULT_ALPHAEARTH_PREVIEW as string | undefined;
    if (raw == null || raw.trim() === '') return true;
    return parseBoolEnv(raw);
  })(),
  /**
   * Optional: WGS84 coordinates for operational weather. If not set, defaults to Hanoi
   * and/or device location (geolocation) on the dashboard header.
   */
  WEATHER_DEFAULT_LAT: parseOptionalCoord(
    import.meta.env.VITE_WEATHER_DEFAULT_LAT as string | undefined,
  ),
  WEATHER_DEFAULT_LON: parseOptionalCoord(
    import.meta.env.VITE_WEATHER_DEFAULT_LON as string | undefined,
  ),
  /**
   * Optional: display label for the operational weather point (e.g., area name).
   * If not set, the UI may show coordinates or a detected place name.
   */
  WEATHER_LOCATION_DISPLAY_NAME: parseOptionalNonEmptyString(
    import.meta.env.VITE_WEATHER_LOCATION_DISPLAY_NAME as string | undefined,
  ),
} as const;

function stripTrailingSlash(url: string): string {
  return url.replace(/\/$/, '');
}

/**
 * Base URL cho `/api/prediction/*` và `/api/simulation/*` khi deploy tách host.
 * Không set `VITE_PREDICTION_SIMULATION_API_BASE_URL` → trả về cùng gateway với {@link env.API_BASE_URL}.
 */
export function getPredictionSimulationApiBaseUrl(): string {
  const raw = env.PREDICTION_SIMULATION_API_BASE_URL;
  if (raw !== '') return stripTrailingSlash(raw);
  return stripTrailingSlash(env.API_BASE_URL);
}

/**
 * Base cho `POST /api/ingest/upload`, `POST /api/ingest/fetch`.
 * Có `VITE_NETCDF_INGESTION_URL` → host đó; không → {@link env.API_BASE_URL} (gateway/BFF).
 */
export function getNetcdfIngestionBaseUrl(): string {
  const raw = env.NETCDF_INGESTION_URL;
  if (raw !== '') return stripTrailingSlash(raw);
  return stripTrailingSlash(env.API_BASE_URL);
}

/**
 * Base URL thực tế cho request NetCDF ingestion (axios `baseURL` + `/api/ingest/...`).
 * Có `VITE_NETCDF_INGESTION_URL` → gọi trực tiếp (vd. `https://ies-netcdf-ingestion.dev.3si.vn`, khớp Swagger).
 * Dev không set: `window.location.origin` → Vite proxy `/api/ingest` tới `VITE_NETCDF_INGESTION_URL` hoặc gateway.
 */
export function getNetcdfIngestionRequestBaseUrl(): string {
  if (env.NETCDF_INGESTION_URL !== '') {
    return getNetcdfIngestionBaseUrl();
  }
  if (import.meta.env.DEV && typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return getNetcdfIngestionBaseUrl();
}

/** `minLng,minLat,maxLng,maxLat` cho Alpha Earth — mặc định = {@link VIETNAM_BOUNDS}. */
function defaultAlphaEarthMetadataBbox(): string {
  const [sw, ne] = VIETNAM_BOUNDS;
  return `${sw[0]},${sw[1]},${ne[0]},${ne[1]}`;
}

/**
 * Base cho metadata tile GEE (`/api/gee/tiles/flood`, `/api/gee/tiles/flood-depth`, …).
 * Thường cùng host với geo-processing: ưu tiên `VITE_GEO_PROCESSING_URL`, fallback `VITE_API_BASE_URL`.
 */
export function getGeeTileServiceBaseUrl(): string {
  const raw = env.GEO_PROCESSING_URL || env.API_BASE_URL;
  return stripTrailingSlash(raw);
}

/**
 * Base cho `GET /api/alpha-earth/tiles` (metadata template).
 * Mặc định giống {@link getGeeTileServiceBaseUrl} (thường :30071). Nếu route nằm trên API khác, set `VITE_ALPHA_EARTH_TILES_BASE_URL`.
 */
export function getAlphaEarthTilesBaseUrl(): string {
  const override = import.meta.env.VITE_ALPHA_EARTH_TILES_BASE_URL as string | undefined;
  if (typeof override === 'string' && override.trim() !== '') {
    return stripTrailingSlash(override);
  }
  return getGeeTileServiceBaseUrl();
}

/**
 * Chuỗi `minLng,minLat,maxLng,maxLat` gửi `GET /api/alpha-earth/tiles` (metadata).
 * Mặc định: toàn bộ {@link VIETNAM_BOUNDS}. Có thể thu hẹp bằng `VITE_ALPHA_EARTH_BBOX`.
 */
export function getAlphaEarthMetadataBbox(): string {
  const raw = import.meta.env.VITE_ALPHA_EARTH_BBOX as string | undefined;
  if (typeof raw === 'string' && raw.trim() !== '') return raw.trim();
  return defaultAlphaEarthMetadataBbox();
}
